import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaTimes, FaSearch, FaStar } from 'react-icons/fa';
import Loading from '../components/loading';
import Helper from '../services/helper.service';
import { useConfirm } from '../components/confirm-dialog';
import { getAllSocieties, saveSociety, deleteSociety, setDefaultSociety } from '../services/api.service';

const useDebouncedValue = (value, delay = 250) => {
    const [v, setV] = useState(value);
    useEffect(() => {
        const id = setTimeout(() => setV(value), delay);
        return () => clearTimeout(id);
    }, [value, delay]);
    return v;
};

const SocietiesListPage = () => {
    const navigate = useNavigate();
    const confirm = useConfirm();

    const [societies, setSocieties] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all'); // all | active | inactive

    useEffect(() => {
        const run = async () => {
            setLoading(true);
            try {
                const data = await getAllSocieties();
                setSocieties(Array.isArray(data) ? data : []);
            } catch (e) {
                Helper.toast("Erreur lors du chargement des sociétés", "error");
            } finally {
                setLoading(false);
            }
        };
        run();
    }, []);

    const debouncedSearch = useDebouncedValue(search, 250);

    const filtered = useMemo(() => {
        const q = debouncedSearch.trim().toLowerCase();
        return societies.filter((s) => {
            const matchesText =
                !q ||
                (s.name || '').toLowerCase().includes(q) ||
                (s.email || '').toLowerCase().includes(q) ||
                (s.siret || '').toLowerCase().includes(q);
            const matchesStatus =
                statusFilter === 'all' ? true : (s.status || 'active') === statusFilter;
            return matchesText && matchesStatus;
        });
    }, [societies, debouncedSearch, statusFilter]);

    const handleAdd = useCallback(() => navigate('/society/new'), [navigate]);
    const handleEdit = useCallback((id) => navigate(`/society/${id}`), [navigate]);

    const handleDelete = async (soc) => {
        if (!soc?._id) return;
        const ok = await confirm({
            title: 'Suppression',
            message: <>Supprimer la société <strong>{soc.name || '(Sans nom)'}</strong> ?</>,
            confirmText: 'Oui',
            cancelText: 'Non',
            tone: 'warning',
        });
        if (!ok) return Helper.toast('Action annulée', 'info');

        const prev = societies;
        setSocieties((p) => p.filter((x) => x._id !== soc._id)); // optimistic
        try {
            await deleteSociety(soc);
        } catch (e) {
            setSocieties(prev); // rollback
            Helper.toast("Erreur lors de la suppression", 'error');
        }
    };

    const toggleActive = async (soc) => {
        const updated = { ...soc, status: soc.status === 'active' ? 'inactive' : 'active' };

        // ---- sanitize before sending ----
        const payload = {
            _id: updated._id,
            status: updated.status,
            name: updated.name,
            email: updated.email,
            website: updated.website,
            phone: updated.phone,
            siret: updated.siret,
            logoUrl: updated.logoUrl,
            bankId:
                updated.bankId && typeof updated.bankId === 'object'
                    ? updated.bankId._id
                    : updated.bankId || undefined,
        };

        const prev = societies;
        setSocieties((p) => p.map((x) => (x._id === soc._id ? updated : x))); // optimistic
        try {
            await saveSociety(payload);
        } catch {
            setSocieties(prev); // rollback
            Helper.toast("Erreur lors de la mise à jour du statut", 'error');
        }
    };

    const handleSetDefault = async (soc) => {
        if (!soc?._id) return;
        // Optimistic update: set this one true, all others false
        const prev = societies;
        setSocieties((list) =>
            list.map((x) => ({ ...x, useByDefault: x._id === soc._id }))
        );
        try {
            await setDefaultSociety(soc._id);
        } catch (e) {
            // rollback on error
            setSocieties(prev);
        }
    };

    return (
        <section className="max-w-7xl mx-auto p-6">
            <div className="mb-6 flex items-center justify-between gap-4">
                <h1 className="text-2xl font-bold text-gray-800">Gestion des sociétés</h1>
                <button
                    onClick={handleAdd}
                    className="bg-primary hover:bg-blue-700 text-white px-4 py-2 rounded shadow"
                >
                    Ajouter une société
                </button>
            </div>

            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-3">
                <div className="flex w-full gap-3">
                    <div className="relative w-full md:max-w-md">
                        <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                        <input
                            type="text"
                            className="w-full border border-gray-300 p-2 pl-9 pr-10 rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Rechercher (nom, email, SIRET)"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            aria-label="Rechercher"
                        />
                        {search && (
                            <button
                                aria-label="Effacer la recherche"
                                onClick={() => setSearch('')}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                            >
                                <FaTimes className="w-4 h-4" />
                            </button>
                        )}
                    </div>

                    <div className="relative w-full sm:w-72">
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="appearance-none bg-white w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            aria-label="Filtrer par statut"
                        >
                            <option value="all">Tous les statuts</option>
                            <option value="active">Actives</option>
                            <option value="inactive">Inactives</option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-500">
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                            </svg>
                        </div>
                    </div>
                </div>

                <div className="text-sm text-gray-600">{filtered.length}&nbsp;résultat(s)</div>
            </div>

            {loading ? (
                <div className="flex justify-center py-20">
                    <Loading />
                </div>
            ) : filtered.length ? (
                <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                    {filtered.map((s) => (
                        <div
                            key={s._id}
                            className="relative p-4 rounded-lg shadow-sm border hover:shadow-md transition bg-white flex flex-col justify-between"
                        >
                            <button
                                type="button"
                                title={s.useByDefault ? "Société par défaut" : "Définir comme société par défaut"}
                                onClick={() => handleSetDefault(s)}
                                className={`absolute right-3 top-3 inline-flex items-center justify-center rounded-full p-2 border ${s.useByDefault ? 'bg-amber-100 border-amber-300' : 'bg-white hover:bg-gray-50'
                                    }`}
                            >
                                <FaStar className={s.useByDefault ? 'text-amber-500' : 'text-gray-400'} />
                            </button>

                            <div>
                                {s.logoUrl && (
                                    <img
                                        src={s.logoUrl}
                                        alt={`Logo ${s.name || ''}`}
                                        className="h-12 object-contain mb-4"
                                    />
                                )}
                                <h2 className="text-lg font-semibold text-gray-800 mb-1">
                                    {s.name || '(Sans nom)'}
                                </h2>

                                {(s.bankId?.name || s.bankId) && (
                                    <p>Banque liée : <span className="text-gray-800">{s.bankId?.name || '—'}</span></p>
                                )}

                                <div className="text-sm text-gray-600 space-y-1">
                                    {s.website && (
                                        <p>
                                            Site :{' '}
                                            <a
                                                href={s.website}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-primary underline"
                                            >
                                                {s.website}
                                            </a>
                                        </p>
                                    )}
                                    {s.email && (
                                        <p>
                                            Email : <span className="text-gray-800">{s.email}</span>
                                        </p>
                                    )}
                                    {s.phone && (
                                        <p>
                                            Tél. : <span className="text-gray-800">{s.phone}</span>
                                        </p>
                                    )}
                                    {s.siret && (
                                        <p>
                                            SIRET : <span className="text-gray-800">{s.siret}</span>
                                        </p>
                                    )}
                                </div>

                                <div className="mt-3">
                                    <span
                                        className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${(s.status || 'active') === 'active'
                                            ? 'bg-green-100 text-green-700'
                                            : 'bg-gray-100 text-gray-700'
                                            }`}
                                    >
                                        {(s.status || 'active') === 'active' ? 'Active' : 'Inactive'}
                                    </span>
                                </div>
                            </div>

                            <div className="mt-4 space-y-2">
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => handleEdit(s._id)}
                                        className="w-full py-2 px-4 bg-gray-800 text-white rounded hover:bg-gray-700 transition"
                                    >
                                        Modifier
                                    </button>
                                    <button
                                        onClick={() => toggleActive(s)}
                                        className="w-full py-2 px-4 bg-white border rounded hover:bg-gray-50 transition"
                                    >
                                        {(s.status || 'active') === 'active' ? 'Désactiver' : 'Activer'}
                                    </button>
                                </div>
                                <button
                                    onClick={() => handleDelete(s)}
                                    className="w-full py-2 px-4 bg-red-600 text-white rounded hover:bg-red-700 transition"
                                >
                                    Supprimer
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <p className="text-center text-gray-500 mt-12">
                    Aucune société trouvée. Cliquez sur &quot;Ajouter une société&quot; pour commencer.
                </p>
            )}
        </section>
    );
};

export default SocietiesListPage;